// @flow

import * as React from 'react';

import { localize, unescaped } from '@ta/common.i18n';
import { getVideoTrackingModule } from '@ta/media.common';
import { RouteConsumer } from '@ta/common.routing';
import * as Import from '@ta/platform.import';
import { InteractionLogger } from '@ta/tracking.interactions';
import { BrandedHeader } from '@ta/modal.headers';
import {
  VideoSidebar,
  FULLSCREEN_FROM_FEED_DATE_LOCAL_STORAGE_KEY,
} from '@ta/social.section-video';
import Storage from '@ta/common.sessionstorage';
import { Abbreviate } from '@ta/common.text';

import type { NavigateOptions, Route } from '@ta/common.routing';

import MatchingItemsMemberContent from './MatchingItemsMemberContent';
import styles from './ShelfVideoSection.less';

type OuterProps = {|
  video: any,
  deleteObject: ?() => void,
  updateModalState: boolean => void,
  uniqueKey: string,
  isSponsored?: boolean,
|};

type ShelfVideoSectionProps = {|
  ...OuterProps,
  route: any,
  navigate: (Route, options?: NavigateOptions) => any,
  isOpen: boolean,
|};

const DeferredVideoPlayer = Import.component.onUsage(
  () => import('@ta/media.video-player'),
  'VideoPlayer',
);

class ShelfVideoSection extends React.Component<ShelfVideoSectionProps> {
  constructor(props) {
    super(props);
    this.contentNode = React.createRef();
    this.videoPlayer = React.createRef();
  }

  onCloseModal = () => {
    if (this.videoPlayer) {
      this.videoPlayer.current.pauseVideo();
    }
    this.props.navigate(this.props.route, { reset: true });
    this.props.updateModalState(false);
  };

  onOpenModal = () => {
    if (this.videoPlayer) {
      Storage.set(FULLSCREEN_FROM_FEED_DATE_LOCAL_STORAGE_KEY, Date.now());
      this.props.navigate({
        ...this.props.route,
        modal: `VideoShelf_${this.props.uniqueKey}_${this.props.video.object.id}`,
        username: this.props.video.object.userProfile.username,
      });
      this.videoPlayer.current.playVideo();
    }
    this.props.updateModalState(true);
    if (this.props.video.object.registerClick) {
      this.props.video.object.registerClick();
    }
  };

  contentNode: React.ElementRef<*>;
  videoPlayer: React.ElementRef<*>;

  render() {
    const { video, isOpen, route } = this.props;
    const { object } = video;

    const durationSeconds = object.sources[0].duration;
    const minutes = Math.floor(durationSeconds / 60000);
    const seconds = Math.floor(durationSeconds / 1000) - minutes * 60;
    const durationDisplay = seconds < 10 ? `${minutes}:0${seconds}` : `${minutes}:${seconds}`;

    const videoTrackingModule = getVideoTrackingModule(route);

    const localizedFeaturedType = unescaped(
      localize('videos_location_featuring_count_v2', {
        '0': styles.poi_text_featured,
        '1': object.orderedLocations?.length || '',
      }),
    ); // Featuring: <span>{0, plural, one{# place} other{# places}}</span>

    return (
      <InteractionLogger>
        {logInteraction => (
          <React.Fragment>
            <div>
              <div
                key={`vw_${object.id}`}
                className={`${styles.video_wrap} ${isOpen ? styles.modalView : styles.inlineView}`}
                ref={this.contentNode}
              >
                <div className={`${styles.video} ${isOpen ? 'ui_columns' : ''}`}>
                  {isOpen && (
                    <div
                      className={`${
                        styles.header
                      } ui_column is-4 is-offset-8 is-3-widescreen is-offset-9-widescreen is-12-mobile`}
                    >
                      <BrandedHeader branding="TA_GREEN" />
                    </div>
                  )}
                  <div
                    className={
                      isOpen
                        ? `${styles.video_wrap} ui_column is-8 is-9-widescreen is-12-mobile`
                        : ''
                    }
                  >
                    <div className={styles.section_wrap}>
                      <DeferredVideoPlayer
                        key={`player_${object.id}`}
                        source={object.sources[0].url}
                        width="100%"
                        height={isOpen ? '100%' : '149px'} // 149 instead of 150 because otherwise rounding makes one pixel of black bar above most videos
                        preload="auto"
                        trackingModule={videoTrackingModule}
                        mediaId={object.id}
                        autoplayWhenVisible={false}
                        ref={this.videoPlayer}
                        poster={object.posterSizes[0].url}
                        muted={false}
                      />
                    </div>
                  </div>
                  {isOpen && (
                    <VideoSidebar
                      video={video}
                      actor={object.userProfile}
                      deleteObject={this.props.deleteObject}
                      onClose={this.onCloseModal}
                    />
                  )}
                </div>
                <div className={styles.duration}>{durationDisplay}</div>
                {!isOpen && (
                  <div
                    className={styles.video_mask}
                    onClick={e => {
                      logInteraction(e, 'feedClick');
                      this.onOpenModal();
                    }}
                  />
                )}
              </div>
              {object.caption && !isOpen && (
                <div
                  className={styles.caption}
                  onClick={e => {
                    logInteraction(e, 'feedClick');
                    this.onOpenModal();
                  }}
                >
                  <Abbreviate text={object.caption} max={50} />
                </div>
              )}
              {object.orderedLocations?.length ? (
                <div className={styles.poi_text}>{localizedFeaturedType}</div>
              ) : null}
            </div>
            <MatchingItemsMemberContent
              userProfile={object.userProfile}
              isSponsored={this.props.isSponsored}
            />
          </React.Fragment>
        )}
      </InteractionLogger>
    );
  }
}

const VideoWithRoute = (props: OuterProps) => (
  <RouteConsumer>
    {({ route: { modal, ...route }, navigate }) =>
      props.video.object && (
        <div className={`${styles.item_container} ui_card`}>
          <ShelfVideoSection
            navigate={navigate}
            route={route}
            isOpen={modal && modal === `VideoShelf_${props.uniqueKey}_${props.video.object.id}`}
            {...props}
          />
        </div>
      )
    }
  </RouteConsumer>
);

VideoWithRoute.defaultProps = {
  deleteObject: null,
};

export default VideoWithRoute;
